import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Tooltip,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Add,
  Delete,
  AdminPanelSettings,
  Security,
  Person,
  MoreVert,
  Edit,
  Refresh
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { UserPermission, PermissionLevel } from '../types';
import { useApp, useErrorHandler } from '../contexts/AppContext';
import { 
  getAllPermissions, 
  updateUserPermissionLevel, 
  addAdminPermission, 
  deleteUserPermission,
  getPermissionLevelLabel,
  getPermissionLevelColor 
} from '../services/permissionService';
import { getUserNamesByEmails } from '../services/userService';

const Permissions: React.FC = () => {
  const [permissions, setPermissions] = useState<UserPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [grantorNames, setGrantorNames] = useState<Record<string, string>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState<UserPermission | null>(null);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [userToUpgrade, setUserToUpgrade] = useState<UserPermission | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<UserPermission | null>(null);

  const { state, isAdmin, refreshPermissions } = useApp();
  const { user, permissions: userPermissions } = state;
  const handleError = useErrorHandler();

  // 권한 목록 로드
  const loadPermissions = useCallback(async () => {
    try {
      setLoading(true);
      const permissionsList = await getAllPermissions();
      setPermissions(permissionsList);
      
      // 부여자 이름 로드
      const emailList = permissionsList.map(p => p.grantedBy).filter(Boolean);
      const uniqueEmails = emailList.filter((email, index) => emailList.indexOf(email) === index);
      if (uniqueEmails.length > 0) {
        const names = await getUserNamesByEmails(uniqueEmails);
        setGrantorNames(names);
      }
    } catch (error: any) {
      handleError(error, '권한 목록 로드');
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    if (isAdmin) {
      loadPermissions();
    }
  }, [isAdmin, loadPermissions]);

  // 이메일 유효성 검증
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // 관리자 권한 추가
  const handleAddAdminPermission = async () => {
    if (!newUserEmail.trim()) {
      setEmailError('이메일을 입력해주세요.');
      return;
    }

    if (!validateEmail(newUserEmail)) {
      setEmailError('올바른 이메일 형식이 아닙니다.');
      return;
    }

    // 기존 권한 확인
    const existingPermission = permissions.find(p => p.email === newUserEmail);
    
    if (existingPermission) {
      if (existingPermission.level === 'admin') {
        setEmailError('이미 관리자 권한이 설정된 사용자입니다.');
        return;
      } else {
        // 기존 권한이 있지만 관리자가 아닌 경우 확인 다이얼로그 표시
        setUserToUpgrade(existingPermission);
        setUpgradeDialogOpen(true);
        setAddDialogOpen(false);
        return;
      }
    }

    // 새 사용자에게 관리자 권한 부여
    try {
      await addAdminPermission(newUserEmail);
      await loadPermissions(); // 목록 새로고침
      await refreshPermissions(); // 현재 사용자 권한 새로고침
      setAddDialogOpen(false);
      setNewUserEmail('');
      setEmailError('');
    } catch (error: any) {
      handleError(error, '관리자 권한 추가');
    }
  };

  // 기존 사용자를 관리자로 업그레이드
  const handleUpgradeToAdmin = async () => {
    if (!userToUpgrade) return;

    try {
      await addAdminPermission(userToUpgrade.email);
      await loadPermissions(); // 목록 새로고침
      await refreshPermissions(); // 현재 사용자 권한 새로고침
      setUpgradeDialogOpen(false);
      setUserToUpgrade(null);
      setNewUserEmail('');
      setEmailError('');
    } catch (error: any) {
      handleError(error, '관리자 권한 업그레이드');
    }
  };

  // 편집 권한 토글 (열람자 ↔ 편집자)
  const handleToggleEditPermission = async (email: string, currentLevel: PermissionLevel) => {
    try {
      // 관리자는 토글하지 않음
      if (currentLevel === 'admin') return;
      
      const newLevel: PermissionLevel = currentLevel === 'viewer' ? 'editor' : 'viewer';
      await updateUserPermissionLevel(email, newLevel);
      await loadPermissions(); // 목록 새로고침
      await refreshPermissions(); // 현재 사용자 권한 새로고침
    } catch (error: any) {
      handleError(error, '권한 변경');
    }
  };

  // 권한 삭제
  const handleDeletePermission = async () => {
    if (!selectedPermission) return;

    try {
      await deleteUserPermission(selectedPermission.email);
      await loadPermissions(); // 목록 새로고침
      await refreshPermissions(); // 현재 사용자 권한 새로고침
      setDeleteDialogOpen(false);
      setSelectedPermission(null);
    } catch (error: any) {
      handleError(error, '권한 삭제');
    }
  };

  // 메뉴 핸들러
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, user: UserPermission) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedUser(null);
  };

  // 권한 레벨 변경
  const handleChangePermissionLevel = async (newLevel: PermissionLevel) => {
    if (!selectedUser) return;

    try {
      await updateUserPermissionLevel(selectedUser.email, newLevel);
      await loadPermissions();
      await refreshPermissions();
      handleMenuClose();
    } catch (error: any) {
      handleError(error, '권한 변경');
    }
  };

  // 메뉴에서 삭제
  const handleDeleteFromMenu = () => {
    if (selectedUser) {
      setSelectedPermission(selectedUser);
      setDeleteDialogOpen(true);
      handleMenuClose();
    }
  };

  // 날짜 포맷팅
  const formatDate = (timestamp: any): string => {
    if (!timestamp) return '';
    
    try {
      const date = timestamp instanceof Date ? timestamp : timestamp.toDate();
      return format(date, 'yyyy.MM.dd', { locale: ko });
    } catch {
      return '';
    }
  };

  // 관리자가 아닌 경우 접근 제한
  if (!isAdmin) {
    return (
      <Box>
        <Alert severity="error">
          이 페이지는 관리자만 접근할 수 있습니다.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* 헤더 */}
      <Box display="flex" justifyContent="between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            권한 관리
          </Typography>
          <Typography variant="body1" color="textSecondary">
            사용자별 편집 권한을 관리합니다.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setAddDialogOpen(true)}
        >
          관리자 권한 추가
        </Button>
      </Box>

      {/* 권한 설명 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Security color="primary" />
            <Typography variant="h6">권한 종류</Typography>
          </Box>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            인원 등록 시 자동으로 열람자 권한이 부여됩니다. 필요에 따라 편집 권한을 토글할 수 있습니다.
          </Alert>
          
          <Box display="flex" gap={3}>
            <Box>
              <Chip 
                icon={<AdminPanelSettings />} 
                label="관리자" 
                color="error" 
                variant="outlined" 
                size="small"
              />
              <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                모든 기능 사용 가능 (직접 추가만 가능)
              </Typography>
            </Box>
            <Box>
              <Chip 
                icon={<Security />} 
                label="편집자" 
                color="warning" 
                variant="outlined" 
                size="small"
              />
              <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                인원 정보 편집 가능
              </Typography>
            </Box>
            <Box>
              <Chip 
                icon={<Person />} 
                label="열람자" 
                color="success" 
                variant="outlined" 
                size="small"
              />
              <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                인원 정보 조회만 가능 (기본 권한)
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* 디버그 정보 및 수동 새로고침 */}
      <Card sx={{ mb: 2, bgcolor: '#f5f5f5' }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="h6" color="textSecondary">
              {process.env.NODE_ENV === 'development' ? '디버그 정보' : '권한 상태'}
            </Typography>
            <Button
              size="small"
              startIcon={<Refresh />}
              onClick={refreshPermissions}
              variant="outlined"
            >
              권한 새로고침
            </Button>
          </Box>
          
          {process.env.NODE_ENV === 'development' && (
            <>
              <Typography variant="body2">현재 사용자: {user?.email}</Typography>
              <Typography variant="body2">isAdmin: {isAdmin ? 'true' : 'false'}</Typography>
              <Typography variant="body2">권한 레벨: {JSON.stringify(userPermissions)}</Typography>
            </>
          )}
          
          {process.env.NODE_ENV !== 'development' && (
            <Typography variant="body2">
              현재 권한: {userPermissions?.level ? getPermissionLevelLabel(userPermissions.level) : '없음'}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* 권한 목록 */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            사용자 권한 목록
          </Typography>
          
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>사용자</TableCell>
                    <TableCell>권한 레벨</TableCell>
                    <TableCell>편집 권한</TableCell>
                    <TableCell>부여일</TableCell>
                    <TableCell>부여자</TableCell>
                    <TableCell align="center">작업</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* 사용자 권한 목록 */}
                  {permissions.map((permission) => (
                    <TableRow key={permission.email}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {permission.name || permission.email}
                          </Typography>
                          {permission.name && (
                            <Typography variant="caption" color="textSecondary">
                              {permission.email}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getPermissionLevelLabel(permission.level || (permission.canEdit ? 'editor' : 'viewer'))} 
                          color={getPermissionLevelColor(permission.level || (permission.canEdit ? 'editor' : 'viewer'))} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell>
                        {permission.level === 'admin' ? (
                          <Typography variant="body2" color="textSecondary">
                            전체 권한
                          </Typography>
                        ) : (
                          <Switch
                            checked={permission.level === 'editor' || permission.canEdit}
                            onChange={() => handleToggleEditPermission(permission.email, permission.level || (permission.canEdit ? 'editor' : 'viewer'))}
                            size="small"
                            color="primary"
                          />
                        )}
                      </TableCell>
                      <TableCell>{formatDate(permission.grantedAt)}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {grantorNames[permission.grantedBy] || permission.grantedBy}
                          </Typography>
                          {grantorNames[permission.grantedBy] && grantorNames[permission.grantedBy] !== permission.grantedBy && (
                            <Typography variant="caption" color="textSecondary">
                              {permission.grantedBy}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="권한 관리">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuClick(e, permission)}
                          >
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  
                  {permissions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="textSecondary">
                          추가된 사용자 권한이 없습니다.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* 권한 추가 다이얼로그 */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>관리자 권한 추가</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="관리자 이메일"
            type="email"
            value={newUserEmail}
            onChange={(e) => {
              setNewUserEmail(e.target.value);
              setEmailError('');
            }}
            error={!!emailError}
            helperText={emailError || '관리자 권한을 부여할 사용자의 이메일을 입력하세요.'}
            sx={{ mt: 1 }}
          />
          
          <Alert severity="warning" sx={{ mt: 2 }}>
            관리자 권한은 모든 기능에 접근할 수 있는 최고 권한입니다. 
            신중하게 부여해주세요.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddDialogOpen(false);
            setNewUserEmail('');
            setEmailError('');
          }}>
            취소
          </Button>
          <Button onClick={handleAddAdminPermission} variant="contained" color="error">
            관리자 권한 추가
          </Button>
        </DialogActions>
      </Dialog>

      {/* 관리자 권한 업그레이드 확인 다이얼로그 */}
      <Dialog open={upgradeDialogOpen} onClose={() => setUpgradeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>관리자 권한 업그레이드</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>{userToUpgrade?.name || userToUpgrade?.email}</strong>님은 현재 <strong>{getPermissionLevelLabel(userToUpgrade?.level || 'viewer')}</strong> 권한을 가지고 있습니다.
          </Alert>
          <Typography>
            이 사용자를 <strong>관리자</strong>로 업그레이드하시겠습니까?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            관리자 권한은 모든 기능에 접근할 수 있는 최고 권한입니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setUpgradeDialogOpen(false);
            setUserToUpgrade(null);
            setAddDialogOpen(true); // 원래 다이얼로그로 돌아가기
          }}>
            취소
          </Button>
          <Button onClick={handleUpgradeToAdmin} variant="contained" color="error">
            관리자로 업그레이드
          </Button>
        </DialogActions>
      </Dialog>

      {/* 권한 관리 메뉴 */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {(() => {
          const currentLevel = selectedUser?.level || (selectedUser?.canEdit ? 'editor' : 'viewer');
          
          return (
            <>
              {currentLevel !== 'admin' && (
                <MenuItem onClick={() => handleChangePermissionLevel('admin')}>
                  <ListItemIcon>
                    <AdminPanelSettings fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText>관리자로 승격</ListItemText>
                </MenuItem>
              )}
              
              {currentLevel !== 'editor' && (
                <MenuItem onClick={() => handleChangePermissionLevel('editor')}>
                  <ListItemIcon>
                    <Security fontSize="small" color="warning" />
                  </ListItemIcon>
                  <ListItemText>편집자로 변경</ListItemText>
                </MenuItem>
              )}
              
              {currentLevel !== 'viewer' && (
                <MenuItem onClick={() => handleChangePermissionLevel('viewer')}>
                  <ListItemIcon>
                    <Person fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText>열람자로 변경</ListItemText>
                </MenuItem>
              )}
            </>
          );
        })()}
        
        <MenuItem onClick={handleDeleteFromMenu} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>권한 삭제</ListItemText>
        </MenuItem>
      </Menu>

      {/* 권한 삭제 확인 다이얼로그 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>권한 삭제 확인</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{selectedPermission?.email}</strong>의 권한을 삭제하시겠습니까?
          </Typography>
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            권한만 삭제되며, 인원 정보는 유지됩니다. 해당 사용자는 더 이상 시스템에 접근할 수 없습니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            취소
          </Button>
          <Button onClick={handleDeletePermission} color="error" variant="contained">
            삭제
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Permissions;
